import { SearchParameters, NLPResponse, AIExtraction, SearchIntent } from './types';
import {
  countryCodeFromProvinceOrState,
  provinceOrStateCodeFromName,
  provinceOrStateCodeFromString,
} from 'utils/province_or_state';
import { buildSystemPrompt, inferPromptIntentHint } from './core/system-prompt-builder';
import {
  detectExplicitNonNorthAmericanLocation,
  inferLocationFromCityAndQuery,
  NON_SUPPORTED_LOCATION_CLARIFICATION,
  NON_SUPPORTED_LOCATION_SUGGESTIONS,
  resolveLocationInSearchParams,
} from './core/location-resolver';
import { AIProvider } from './providers';
import {
  EMPTY_PROMPT_TELEMETRY,
  measurePromptTelemetry,
  promptTelemetryForQueryOnly,
  promptTelemetryPayload,
  type PromptTelemetryMetrics,
} from './core/nlp-prompt-telemetry';

const isNlpPromptTelemetryEnabled = (): boolean => process.env.NLP_PROMPT_TELEMETRY === 'true';

const logNlpTelemetry = (
  event: string,
  payload: Record<string, unknown>,
  promptMetrics: PromptTelemetryMetrics = EMPTY_PROMPT_TELEMETRY
): void => {
  if (!isNlpPromptTelemetryEnabled()) {
    return;
  }

  try {
    console.info(
      `[conversational-search][${event}]`,
      JSON.stringify({
        ...promptTelemetryPayload(promptMetrics),
        ...payload,
      })
    );
  } catch (_error) {
    console.info(`[conversational-search][${event}]`, {
      ...promptTelemetryPayload(promptMetrics),
      ...payload,
    });
  }
};

const logPhase2Debug = (message: string, payload?: Record<string, unknown>): void => {
  if (process.env.NODE_ENV !== 'development') {
    return;
  }

  if (payload) {
    console.log(`[conversational-search][phase2] ${message}`, payload);
    return;
  }

  console.log(`[conversational-search][phase2] ${message}`);
};

// Supported countries for property searches
export const SUPPORTED_COUNTRIES = [
  { code: 'US', name: 'United States' },
  { code: 'CA', name: 'Canada' },
] as const;

// ============================================================================
// INTENT-BASED MERGE LOGIC
// ============================================================================

/**
 * Merges newFilters into currentParams based on the intent.
 * This is the core of the intent-based architecture - AI extracts, code merges.
 */
export const mergeFiltersBasedOnIntent = (
  intent: SearchIntent,
  currentParams: SearchParameters,
  newFilters: Partial<SearchParameters>,
  removeFilters?: string[]
): SearchParameters => {
  switch (intent) {
    case 'new_search':
      // For new search, use newFilters as the base (fresh start)
      return { ...newFilters } as SearchParameters;

    case 'add_filter':
      // For add, always MERGE (especially homeType)
      return mergeParamsAdditive(currentParams, newFilters);

    case 'confirm':
      // For confirm, merge newFilters into currentParams
      return mergeParamsAdditive(currentParams, newFilters);

    case 'modify_filter':
      // For modify, replace the specific filter values (including homeType)
      return mergeParamsReplace(currentParams, newFilters);

    case 'remove_filter':
      // For remove, delete the specified filters from currentParams
      return removeFiltersFromParams(currentParams, removeFilters || []);

    case 'execute':
    case 'clarify':
      // For execute/clarify, keep currentParams unchanged
      return { ...currentParams };

    default:
      // Default: merge newFilters into currentParams
      return mergeParamsAdditive(currentParams, newFilters);
  }
};

/**
 * Helper to ADDITIVELY merge newFilters into currentParams
 * homeType is MERGED (preserves existing true values, adds new ones)
 */
const mergeParamsAdditive = (
  currentParams: SearchParameters,
  newFilters: Partial<SearchParameters>
): SearchParameters => {
  const merged = { ...currentParams };

  Object.entries(newFilters).forEach(([key, value]) => {
    if (value === null) {
      // Explicit removal
      delete (merged as Record<string, unknown>)[key];
    } else if (key === 'homeType' && typeof value === 'object' && value !== null) {
      // For homeType in additive mode, always MERGE
      // "add condos" with existing house: true → {house: true, condo: true}
      merged.homeType = { ...merged.homeType, ...value };
    } else if (value !== undefined) {
      (merged as Record<string, unknown>)[key] = value;
    }
  });

  return merged;
};

/**
 * Helper to merge newFilters into currentParams with REPLACE semantics for homeType
 * homeType is REPLACED (when switching property types)
 */
const mergeParamsReplace = (
  currentParams: SearchParameters,
  newFilters: Partial<SearchParameters>
): SearchParameters => {
  const merged = { ...currentParams };

  Object.entries(newFilters).forEach(([key, value]) => {
    if (value === null) {
      // Explicit removal
      delete (merged as Record<string, unknown>)[key];
    } else if (key === 'homeType' && typeof value === 'object' && value !== null) {
      // For homeType in replace mode, check for explicit falses
      const newHomeType = value as SearchParameters['homeType'];
      const hasExplicitFalse = Object.values(newHomeType || {}).some(v => v === false);
      
      if (hasExplicitFalse) {
        // "condos instead of houses" → merge with falses applied
        merged.homeType = { ...merged.homeType, ...newHomeType };
      } else {
        // "show me only condos" → replace entirely
        merged.homeType = newHomeType;
      }
    } else if (value !== undefined) {
      (merged as Record<string, unknown>)[key] = value;
    }
  });

  return merged;
};

/**
 * Helper to remove filters from currentParams by path
 */
// Default homeType when all property types are selected
const DEFAULT_HOME_TYPE = {
  house: true,
  condo: true,
  townhouse: true,
  farm: true,
  land: true,
  commercial: true,
};

const removeFiltersFromParams = (
  currentParams: SearchParameters,
  filterPaths: string[]
): SearchParameters => {
  const result = { ...currentParams };

  filterPaths.forEach(path => {
    if (path === 'homeType') {
      // Special case: removing homeType resets to default (all types selected)
      result.homeType = { ...DEFAULT_HOME_TYPE };
    } else if (path.includes('.')) {
      // Handle nested paths like "homeType.condo"
      const parts = path.split('.');
      if (parts.length === 2) {
        const [parent, child] = parts;
        if (parent === 'homeType' && result.homeType) {
          result.homeType = { ...result.homeType };
          delete (result.homeType as Record<string, unknown>)[child];
        } else if (parent === 'additional' && result.additional) {
          // Handle additional.condoOrTownhouse.locker etc.
          const additionalCopy = { ...result.additional };
          if (additionalCopy.condoOrTownhouse) {
            additionalCopy.condoOrTownhouse = { ...additionalCopy.condoOrTownhouse };
            delete (additionalCopy.condoOrTownhouse as Record<string, unknown>)[child];
          }
          result.additional = additionalCopy;
        }
      }
    } else {
      // Simple top-level path
      delete (result as Record<string, unknown>)[path];
    }
  });

  return result;
};

export const normalizeAiSearchParams = (params: Record<string, unknown>): SearchParameters => {
  const normalized: Record<string, unknown> = { ...params };
  const hasStateKey = Object.prototype.hasOwnProperty.call(normalized, 'state');
  const rawState = typeof normalized['state'] === 'string' ? normalized['state'].trim() : '';
  const hasProvince = typeof normalized['province'] === 'string' && normalized['province'].trim().length > 0;

  if (rawState && !hasProvince) {
    let stateCode = provinceOrStateCodeFromString(rawState);

    if (!stateCode) {
      stateCode = provinceOrStateCodeFromName(rawState);
    }

    if (stateCode) {
      normalized['province'] = stateCode.toUpperCase();
      if (!normalized['country'] || typeof normalized['country'] !== 'string') {
        normalized['country'] = countryCodeFromProvinceOrState(stateCode);
      }
    }
  }

  if (typeof normalized['country'] === 'string') {
    const uppercaseCountry = normalized['country'].toUpperCase();
    normalized['country'] = uppercaseCountry;
  }

  if (hasStateKey) {
    delete normalized['state'];
  }

  const provinceValue = normalized['province'];
  if (typeof provinceValue === 'string') {
    const uppercaseProvince = provinceValue.toUpperCase();
    normalized['province'] = uppercaseProvince;
    const inferredCountry = countryCodeFromProvinceOrState(uppercaseProvince.toLowerCase());
    const currentCountry = typeof normalized['country'] === 'string' ? normalized['country'] : null;
    if (!currentCountry) {
      normalized['country'] = inferredCountry;
    } else {
      normalized['country'] = currentCountry.toUpperCase();
    }
  } else if (!normalized['country'] || typeof normalized['country'] !== 'string') {
    delete normalized['country'];
  }

  return normalized as SearchParameters;
};


// buildSystemPrompt is now imported from './core/system-prompt-builder'
// Re-export for backward compatibility with tests
export { buildSystemPrompt, inferPromptIntentHint } from './core/system-prompt-builder';
export type { BuildSystemPromptOptions, PromptIntentVariant } from './core/system-prompt-builder';

// Helper function to format price for display
export const formatPriceForDisplay = (price: number): string => {
  if (price >= 1000000) {
    const millions = price / 1000000;
    return millions % 1 === 0 ? `$${millions}M` : `$${millions.toFixed(1)}M`;
  } else if (price >= 1000) {
    const thousands = price / 1000;
    return thousands % 1 === 0 ? `$${thousands}k` : `$${thousands.toFixed(0)}k`;
  }
  return `$${price.toLocaleString()}`;
};

export const extractBasicParameters = (query: string): SearchParameters => {
  const params: SearchParameters = {};
  const lowerQuery = query.toLowerCase();

  // Avoid extracting a single city if multiple cities are present
  const multiCities = detectMultipleCities(lowerQuery);
  if (!multiCities) {
    const locationInfo = extractLocationFromQuery(lowerQuery);
    if (locationInfo) {
      params.city = locationInfo.city;
      params.province = locationInfo.province;
      params.country = locationInfo.country;
    }
  }

  // Property type detection - distinguish between specific types vs general property requests
  if (lowerQuery.includes('properties') || lowerQuery.includes('real estate') || lowerQuery.includes('listings')) {
    // General property request - keep all types selected (default state)
    params.homeType = {
      house: true,
      condo: true,
      townhouse: true,
      farm: true,
      land: true,
      commercial: true,
    };
  } else if (lowerQuery.includes('house') || lowerQuery.includes('detached') || lowerQuery.includes('single family')) {
    // Specific house request - only houses
    params.homeType = { house: true };
  } else if (lowerQuery.includes('condo') || lowerQuery.includes('apartment') || lowerQuery.includes('unit')) {
    // Specific condo request - only condos
    params.homeType = { condo: true };
  } else if (lowerQuery.includes('townhouse') || lowerQuery.includes('townhome') || lowerQuery.includes('row house')) {
    // Specific townhouse request - only townhouses
    params.homeType = { townhouse: true };
  } else if (lowerQuery.includes('farm') || lowerQuery.includes('farmhouse') || lowerQuery.includes('rural')) {
    // Specific farm request - only farms
    params.homeType = { farm: true };
  } else if (lowerQuery.includes('land') || lowerQuery.includes('lot') || lowerQuery.includes('vacant')) {
    // Specific land request - only land
    params.homeType = { land: true };
  } else if (lowerQuery.includes('commercial') || lowerQuery.includes('office') || lowerQuery.includes('retail')) {
    // Specific commercial request - only commercial
    params.homeType = { commercial: true };
  }

  const pricePatterns = [
    // Million patterns
    /under\s*\$?(\d+(?:\.\d+)?)[mM]/g, // "under 1.5M"
    /(\d+(?:\.\d+)?)[mM]\s*(?:and\s+under|or\s+less|maximum|max|up\s+to)/g, // "1.5M and under"
    /over\s*\$?(\d+(?:\.\d+)?)[mM]/g, // "over 1M"
    /between\s*\$?(\d+(?:\.\d+)?)[mM]\s*and\s*\$?(\d+(?:\.\d+)?)[mM]/g, // "between 1M and 2M"
    /(\d+(?:\.\d+)?)[mM]\s*to\s*(\d+(?:\.\d+)?)[mM]/g, // "1M to 2M"
    /(\d+(?:\.\d+)?)[mM]\s*-\s*(\d+(?:\.\d+)?)[mM]/g, // "1M-2M"
    /(\d+(?:\.\d+)?)[mM]/g, // Simple "1.5M" - default to max price
    
    // Thousand patterns
    /under\s*\$?(\d+)[kK]/g, // "under 800k"
    /(\d+)[kK]\s*(?:and\s+under|or\s+less|maximum|max|up\s+to)/g, // "800k and under"
    /over\s*\$?(\d+)[kK]/g, // "over 500k"
    /between\s*\$?(\d+)[kK]\s*and\s*\$?(\d+)[kK]/g, // "between 500k and 800k"
    /(\d+)[kK]\s*to\s*(\d+)[kK]/g, // "500k to 800k"
    /(\d+)[kK]\s*-\s*(\d+)[kK]/g, // "500k-800k"
    /(\d+)[kK]/g, // Simple "800k" - default to max price
  ];

  for (const pattern of pricePatterns) {
    const matches = [...lowerQuery.matchAll(pattern)];
    for (const match of matches) {
      const isMillions = pattern.source.includes('[mM]');
      const multiplier = isMillions ? 1000000 : 1000;
      
      if (pattern.source.includes('between') || pattern.source.includes('to') || pattern.source.includes('-')) {
        // Range pattern
        if (match[1] && match[2]) {
          params.priceMin = parseFloat(match[1]) * multiplier;
          params.priceMax = parseFloat(match[2]) * multiplier;
        }
      } else if (pattern.source.includes('under') || pattern.source.includes('maximum') || pattern.source.includes('max')) {
        // Maximum price
        if (match[1]) {
          params.priceMax = parseFloat(match[1]) * multiplier;
        }
      } else if (pattern.source.includes('over')) {
        // Minimum price
        if (match[1]) {
          params.priceMin = parseFloat(match[1]) * multiplier;
        }
      } else {
        // Default to maximum price (most common case)
        if (match[1]) {
          params.priceMax = parseFloat(match[1]) * multiplier;
        }
      }
      break; // Use first match found
    }
    if (params.priceMin !== undefined || params.priceMax !== undefined) break;
  }

  // Bedroom extraction - default to minimum (+ format) unless "exactly" is specified
  const bedroomMatch = lowerQuery.match(/(\d+)\+?\s*(?:bedroom|bed|br)/);
  if (bedroomMatch) {
    const num = bedroomMatch[1];
    // Check for explicit "exactly" keyword
    const isExact = lowerQuery.includes('exactly') && lowerQuery.includes(`${num}`);
    params.bedrooms = isExact ? num : `${num}+`;
  }

  // Bathroom extraction - default to minimum (+ format) unless "exactly" is specified
  const bathroomMatch = lowerQuery.match(/(\d+)\+?\s*(?:bathroom|bath|ba)/);
  if (bathroomMatch) {
    const num = bathroomMatch[1];
    // Check for explicit "exactly" keyword
    const isExact = lowerQuery.includes('exactly') && lowerQuery.includes(`${num}`);
    params.bathrooms = isExact ? num : `${num}+`;
  }

  // Parking spaces extraction
  const parkingPatterns = [
    /(\d+)\+?\s*(?:parking|garage|car)/g,
    /(\d+)\+?\s*(?:space|spot)s?/g,
    /parking\s+for\s+(\d+)\+?/g,
  ];
  
  for (const pattern of parkingPatterns) {
    const matches = [...lowerQuery.matchAll(pattern)];
    for (const match of matches) {
      if (match[1]) {
        const num = match[1];
        params.parkingSpaces = lowerQuery.includes(`${num}+`) ? `${num}+` : num;
        break;
      }
    }
    if (params.parkingSpaces) break;
  }

  // Square footage extraction
  const sqftPatterns = [
    /(\d+)\+?\s*(?:sq\s*ft|sqft|square\s+feet)/g,
    /(\d+)\s*to\s*(\d+)\s*(?:sq\s*ft|sqft|square\s+feet)/g,
    /between\s*(\d+)\s*and\s*(\d+)\s*(?:sq\s*ft|sqft|square\s+feet)/g,
    /over\s*(\d+)\s*(?:sq\s*ft|sqft|square\s+feet)/g,
    /under\s*(\d+)\s*(?:sq\s*ft|sqft|square\s+feet)/g,
  ];

  for (const pattern of sqftPatterns) {
    const matches = [...lowerQuery.matchAll(pattern)];
    for (const match of matches) {
      if (pattern.source.includes('to') || pattern.source.includes('between')) {
        // Range pattern
        if (match[1] && match[2]) {
          params.sqftMin = parseInt(match[1]);
          params.sqftMax = parseInt(match[2]);
        }
      } else if (pattern.source.includes('over')) {
        // Minimum sqft
        if (match[1]) {
          params.sqftMin = parseInt(match[1]);
        }
      } else if (pattern.source.includes('under')) {
        // Maximum sqft
        if (match[1]) {
          params.sqftMax = parseInt(match[1]);
        }
      } else {
        // Default to minimum sqft
        if (match[1]) {
          params.sqftMin = parseInt(match[1]);
        }
      }
      break;
    }
    if (params.sqftMin !== undefined || params.sqftMax !== undefined) break;
  }

  // Amenities extraction
  if (lowerQuery.includes('garage') || lowerQuery.includes('parking')) {
    params.garage = true;
  }
  if (lowerQuery.includes('pool') || lowerQuery.includes('swimming')) {
    params.pool = true;
  }
  if (lowerQuery.includes('fireplace')) {
    params.fireplace = true;
  }
  if (lowerQuery.includes('waterfront') || lowerQuery.includes('water front') || lowerQuery.includes('lakefront')) {
    params.waterfront = true;
  }
  if (lowerQuery.includes('open house') || lowerQuery.includes('openhouse')) {
    params.openHouse = true;
  }

  // Rental vs Buy detection
  if (lowerQuery.includes('rent') || lowerQuery.includes('rental') || lowerQuery.includes('lease')) {
    params.rental = true;
  } else if (lowerQuery.includes('buy') || lowerQuery.includes('purchase') || lowerQuery.includes('sale')) {
    params.rental = false;
  }

  // Status detection (for sale vs sold)
  if (lowerQuery.includes('sold') || lowerQuery.includes('recently sold')) {
    params.status = 'not-available-sold';
  } else if (lowerQuery.includes('for sale') || lowerQuery.includes('available')) {
    params.status = 'available';
  }

  // Date range extraction (basic patterns)
  const datePatterns = [
    /listed\s+(?:in\s+)?(?:the\s+)?last\s+(\d+)\s+days?/g,
    /listed\s+(?:in\s+)?(?:the\s+)?past\s+(\d+)\s+days?/g,
    /listed\s+(?:within\s+)?(\d+)\s+days?/g,
    /new\s+listing/g,
    /recently\s+listed/g,
  ];

  for (const pattern of datePatterns) {
    const matches = [...lowerQuery.matchAll(pattern)];
    for (const match of matches) {
      if (pattern.source.includes('new') || pattern.source.includes('recently')) {
        // Default to last 7 days for "new" or "recently"
        const sevenDaysAgo = new Date();
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
        params.listedSince = sevenDaysAgo.toISOString().split('T')[0];
      } else if (match[1]) {
        // Specific number of days
        const daysAgo = parseInt(match[1]);
        const date = new Date();
        date.setDate(date.getDate() - daysAgo);
        params.listedSince = date.toISOString().split('T')[0];
      }
      break;
    }
    if (params.listedSince) break;
  }

  return params;
};

// Helper function to extract location information from query
function extractLocationFromQuery(query: string): { city: string; province: string; country: string } | null {
  // Common patterns to find city names
  const locationPatterns = [
    /(?:in|at|near|around)\s+([a-zA-Z\s]+?)(?:\s|,|$|under|over|with|that|and|or|for)/,
    /(?:houses?|condos?|townhouses?|properties?)\s+(?:in|at|near|around)\s+([a-zA-Z\s]+?)(?:\s|,|$|under|over|with|that|and|or|for)/,
    /show\s+.*?\s+(?:in|at|near|around)\s+([a-zA-Z\s]+?)(?:\s|,|$|under|over|with|that|and|or|for)/,
  ];

  for (const pattern of locationPatterns) {
    const match = query.match(pattern);
    if (match && match[1]) {
      const potentialLocation = match[1].trim();
      
      // Skip common words that aren't cities
      const skipWords = ['the', 'a', 'an', 'and', 'or', 'but', 'with', 'that', 'this', 'these', 'those', 'good', 'nice', 'great'];
      if (skipWords.includes(potentialLocation.toLowerCase()) || potentialLocation.length < 2) {
        continue;
      }
      
      // Clean up the city name (capitalize first letter of each word)
      const cleanCityName = potentialLocation
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
      
      // Determine province/state and country based on context or default to Canada
      const locationInfo = inferLocationFromCityAndQuery(cleanCityName, query);
      if (locationInfo) {
        return locationInfo;
      }

      return null;
    }
  }
  
  return null;
}

// Helper: robustly detect when multiple distinct cities are present
function capitalizeWords(input: string): string {
  return input
    .split(' ')
    .filter(Boolean)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

export function detectHelpQuery(query: string): boolean {
  const lowerQuery = query.toLowerCase().trim();
  const helpKeywords = [
    'what can you do',
    'what else can you do',
    'what can i ask',
    'what can you help',
    'how can you help',
    'what do you do',
    'what are you',
    'help',
    'how does this work',
    'how do i use',
    'what filters',
    'what can i search',
    'show me examples',
    'give me examples',
    'what are examples',
    'what can i say',
    'any suggestions',
    'any other suggestions',
  ];
  
  return helpKeywords.some(keyword => lowerQuery.includes(keyword));
}

const REMOVE_ALL_FILTER_PATHS = [
  'homeType',
  'bedrooms',
  'bathrooms',
  'priceMin',
  'priceMax',
  'pool',
  'garage',
  'fireplace',
  'waterfront',
  'openHouse',
  'sqftMin',
  'sqftMax',
  'parkingSpaces',
] as const;

const REMOVE_FILTER_RULES: Array<{ pattern: RegExp; paths: string[] }> = [
  { pattern: /\b(no\s+price\s+limit|remove\s+(the\s+)?price|clear\s+price|without\s+price)\b/i, paths: ['priceMin', 'priceMax']},
  { pattern: /\b(remove|clear|without|no)\s+(the\s+)?bed(room)?s?\b/i, paths: ['bedrooms']},
  { pattern: /\b(remove|clear|without|no)\s+(the\s+)?bath(room)?s?\b/i, paths: ['bathrooms']},
  { pattern: /\b(remove|clear|without|no)\s+(the\s+)?garage\b/i, paths: ['garage']},
  { pattern: /\b(remove|clear|without|no)\s+(the\s+)?pool\b/i, paths: ['pool']},
  { pattern: /\b(remove|clear|without|no)\s+(the\s+)?fireplace\b/i, paths: ['fireplace']},
  { pattern: /\b(remove|clear|without|no)\s+(the\s+)?waterfront\b/i, paths: ['waterfront']},
  { pattern: /\b(remove|clear|without|no)\s+(the\s+)?open\s*house\b/i, paths: ['openHouse']},
  { pattern: /\b(remove|clear|without|no)\s+(the\s+)?parking\b/i, paths: ['parkingSpaces']},
  { pattern: /\b(remove|clear|without|no)\s+(the\s+)?size\b/i, paths: ['sqftMin', 'sqftMax']},
  { pattern: /\b(remove|clear)\s+(the\s+)?(property\s+)?type\b/i, paths: ['homeType']},
];

function detectDeterministicRemovePaths(query: string): string[] | null {
  const trimmed = query.trim();

  if (!trimmed) {
    return null;
  }

  if (/\b(clear|remove)\s+(all|every)\s+filters?\b/i.test(trimmed)) {
    return [...REMOVE_ALL_FILTER_PATHS];
  }

  const matchedPaths = new Set<string>();

  REMOVE_FILTER_RULES.forEach(rule => {
    if (rule.pattern.test(trimmed)) {
      rule.paths.forEach(path => matchedPaths.add(path));
    }
  });

  return matchedPaths.size > 0 ? Array.from(matchedPaths) : null;
}

function detectDeterministicExecuteIntent(query: string): boolean {
  const normalized = query.toLowerCase().trim().replace(/[!?.,]/g, '');

  if (!normalized) {
    return false;
  }

  const executePatterns = [
    /^(search|search now|run search|execute search)$/,
    /^(show results|show me results|show listings|show me listings)$/,
    /^(find (them|properties|homes))$/,
    /^(go ahead|lets go|let's go)$/,
  ];

  return executePatterns.some(pattern => pattern.test(normalized));
}

export function detectMultipleCities(query: string): Array<{ city: string; province: string; country: string }> | null {
  // Words that should never appear in city names - filter out false positives
  const invalidCityWords = ['with', 'a', 'the', 'and', 'or', 'garage', 'pool', 'bedroom', 'bathroom', 'bath', 'bed', 'house', 'condo', 'property', 'under', 'over', 'near', 'around'];
  
  const isValidCityName = (name: string): boolean => {
    const words = name.toLowerCase().split(/\s+/);
    // Reject if contains invalid words
    if (words.some(w => invalidCityWords.includes(w))) return false;
    // Reject if too long (cities rarely have more than 3 words)
    if (words.length > 3) return false;
    // Reject if too short (single letter)
    if (name.trim().length < 2) return false;
    return true;
  };
  
  // Try common multi-city phrasings
  const patterns = [
    // between X and Y
    /between\s+([a-zA-Z\s]+?)\s+(?:and|to)\s+([a-zA-Z\s]+?)(?=$|,|\s(?:under|over|with|that|or|for)\b)/,
    // in/at/near/around X and|,|to Y
    /(?:in|at|near|around)\s+([a-zA-Z\s]+?)\s+(?:and|,|to)\s+([a-zA-Z\s]+?)(?=$|,|\s(?:under|over|with|that|or|for)\b)/,
  ];
  for (const pattern of patterns) {
    const m = query.match(pattern);
    if (m && m[1] && m[2]) {
      const first = capitalizeWords(m[1].trim());
      const second = capitalizeWords(m[2].trim());
      if (!first || !second || first.toLowerCase() === second.toLowerCase()) continue;
      
      // Validate that both look like city names
      if (!isValidCityName(first) || !isValidCityName(second)) continue;
			
      const firstResolved = inferLocationFromCityAndQuery(first, query);
      const secondResolved = inferLocationFromCityAndQuery(second, query);
      // Ensure they look like cities (heuristic: both resolved)
      if (firstResolved && secondResolved) {
        return [firstResolved, secondResolved];
      }
    }
  }
  return null;
}

/**
 * Generates an explicit list of filters that are already applied.
 * This helps the AI avoid suggesting filters that are already set.
 */
/**
 * Generates a prioritized list of suggestions based on what's MISSING from current filters.
 * Priority: Budget > Bedrooms > Bathrooms > Features > Other
 * Returns max 3 suggestions.
 */
function generateSuggestFromList(params: SearchParameters): string[] {
  const suggestions: string[] = [];
  
  // Priority 1: Budget (if not set)
  if (!params.priceMin && !params.priceMax) {
    suggestions.push('What\'s your budget?');
  }
  
  // Priority 2: Bedrooms (if not set)
  if (!params.bedrooms) {
    suggestions.push('How many bedrooms?');
  }
  
  // Priority 3: Bathrooms (if not set)
  if (!params.bathrooms) {
    suggestions.push('How many bathrooms?');
  }
  
  // Priority 4: Features (if none set)
  const hasAnyFeature = params.pool || params.garage || params.fireplace || params.waterfront || params.openHouse;
  if (!hasAnyFeature && suggestions.length < 3) {
    suggestions.push('Any features? (pool, garage, fireplace)');
  }
  
  // Priority 5: Size (if not set)
  if (!params.sqftMin && !params.sqftMax && suggestions.length < 3) {
    suggestions.push('Any size requirements?');
  }
  
  // Priority 6: Parking (if not set)
  if (!params.parkingSpaces && suggestions.length < 3) {
    suggestions.push('Parking requirements?');
  }
  
  // Priority 7: Property type (if not set)
  const hasPropertyType = params.homeType && 
    (params.homeType.house || params.homeType.condo || params.homeType.townhouse || 
     params.homeType.farm || params.homeType.land || params.homeType.commercial);
  if (!hasPropertyType && suggestions.length < 3) {
    suggestions.push('What property type?');
  }
  
  // Priority 8: Location (if not set) - usually set first, but just in case
  if (!params.city && suggestions.length < 3) {
    suggestions.push('What city?');
  }
  
  // Return max 3 suggestions
  return suggestions.slice(0, 3);
}

export const processQuery = async (
  userQuery: string,
  currentParams: SearchParameters = {}, 
  provider: AIProvider,
  recentMessages?: Array<{ role: 'user' | 'assistant'; content: string }>
): Promise<NLPResponse> => {
  const processingStartedAtMs = Date.now();
  const trimmedUserQuery = userQuery.trim();
  const currentParamsCount = Object.keys(currentParams).length;
  const recentMessagesCount = recentMessages?.length || 0;
  let llmPromptMetrics: PromptTelemetryMetrics = promptTelemetryForQueryOnly(trimmedUserQuery);

  // Handle help queries up front: return guidance without calling OpenAI
  if (detectHelpQuery(userQuery)) {
    // Generate context-aware suggestions based on what's already set
    const suggestions: string[] = [];
    
    // Check what filters are already set
    const hasLocation = !!currentParams.city;
    const hasPropertyType = currentParams.homeType && Object.values(currentParams.homeType).some(v => v === true);
    const hasPrice = !!(currentParams.priceMin || currentParams.priceMax);
    const hasBedrooms = !!currentParams.bedrooms;
    const hasBathrooms = !!currentParams.bathrooms;
    const hasAmenities = !!(currentParams.garage || currentParams.pool || currentParams.fireplace || currentParams.waterfront || currentParams.openHouse);
    const hasSqft = !!(currentParams.sqftMin || currentParams.sqftMax);
    
    // Build suggestions for filters that are NOT set
    if (!hasLocation) {
      suggestions.push('Search for properties in Miami', 'Find houses in Austin, TX');
    }
    if (!hasPropertyType) {
      suggestions.push('Show me houses', 'Find condos');
    }
    if (!hasPrice) {
      suggestions.push('Properties under $800k', 'Homes between $500k and $1M');
    }
    if (!hasBedrooms) {
      suggestions.push('3 bedroom properties', 'Homes with 2+ bedrooms');
    }
    if (!hasBathrooms) {
      suggestions.push('2 bathroom homes');
    }
    if (!hasAmenities) {
      suggestions.push('Homes with a pool', 'Properties with garage');
    }
    if (!hasSqft) {
      suggestions.push('Homes over 2000 sqft');
    }
    
    // If all major filters are set, suggest combinations or refinements
    if (hasLocation && hasPropertyType && hasPrice && hasBedrooms) {
      suggestions.push('Add a pool', 'Show me waterfront properties', 'Properties with garage');
    }
    
    // Default examples if no specific suggestions
    if (suggestions.length === 0) {
      suggestions.push('3-bedroom houses in Miami under $800k', 'Find condos with a pool', 'Properties in Austin, TX');
    }
    
    // Limit to 4-5 suggestions
    const exampleQueries = suggestions.slice(0, 5);
    
    // Build concise help message
    const helpMessage = 'You can tell me the location, property type, price, and any features you want. For example: "3-bedroom houses in Miami under $800k"';

    logNlpTelemetry(
      'path_decision',
      {
        path: 'help_short_circuit',
        elapsedMs: Date.now() - processingStartedAtMs,
        currentParamsCount,
        recentMessagesCount,
      },
      promptTelemetryForQueryOnly(trimmedUserQuery)
    );

    return {
      searchParams: currentParams, // Preserve current params
      intent: 'clarify' as SearchIntent,
      confidence: 1.0,
      needsClarification: false,
      clarification: undefined,
      message: helpMessage,
      followUpSuggestions: exampleQueries,
      isReadyToSearch: false,
      context: { accumulatedParams: currentParams },
    };
  }

  if (detectExplicitNonNorthAmericanLocation(trimmedUserQuery)) {
    logNlpTelemetry(
      'path_decision',
      {
        path: 'non_supported_country_short_circuit',
        elapsedMs: Date.now() - processingStartedAtMs,
        currentParamsCount,
        recentMessagesCount,
      },
      promptTelemetryForQueryOnly(trimmedUserQuery)
    );

    return {
      searchParams: { ...currentParams },
      intent: 'clarify' as SearchIntent,
      confidence: 0.95,
      needsClarification: true,
      clarification: NON_SUPPORTED_LOCATION_CLARIFICATION,
      followUpSuggestions: [...NON_SUPPORTED_LOCATION_SUGGESTIONS],
      isReadyToSearch: false,
      context: { accumulatedParams: currentParams },
    };
  }

  // Handle multi-city queries up front: ask for clarification and do NOT default to old filters
  const lowerUserQuery = userQuery.toLowerCase();
  const multiCities = detectMultipleCities(lowerUserQuery);
  if (multiCities && multiCities.length >= 2) {
    const [a, b] = multiCities;
    const suggestions: string[] = [];
    const format = (loc: { city: string; province: string; country: string }) => {
      const countryLabel = loc.country === 'CA' ? 'Canada' : loc.country === 'US' ? 'USA' : loc.country;
      return `${loc.city}, ${loc.province} (${countryLabel})`;
    };
    suggestions.push(format(a), format(b));

    logNlpTelemetry(
      'path_decision',
      {
        path: 'multi_city_short_circuit',
        elapsedMs: Date.now() - processingStartedAtMs,
        detectedCities: multiCities.length,
        currentParamsCount,
        recentMessagesCount,
      },
      promptTelemetryForQueryOnly(trimmedUserQuery)
    );

    return {
      searchParams: { ...currentParams }, // preserve, but don't change
      intent: 'clarify' as SearchIntent,
      confidence: 0.7,
      needsClarification: true,
      clarification: 'I can search one city at a time. Please tell me which city you\'d like to search.',
      followUpSuggestions: suggestions,
      isReadyToSearch: false,
      context: { accumulatedParams: currentParams },
    };
  }

  // Deterministic pre-LLM branch (remove_filter / execute). Not gated on telemetry.
  {
    const removePaths = detectDeterministicRemovePaths(userQuery);
    if (removePaths) {
      const mergedParams = mergeFiltersBasedOnIntent('remove_filter', currentParams, {}, removePaths);
      const hasActiveParams = Object.keys(currentParams).length > 0;
      const message = hasActiveParams
        ? 'Removed those filter(s).'
        : 'No active filters to remove yet.';

      logPhase2Debug('deterministic remove_filter matched', {
        query: trimmedUserQuery,
        removePaths,
        hadActiveParams: hasActiveParams,
      });

      logNlpTelemetry(
        'path_decision',
        {
          path: 'phase2_remove_filter_short_circuit',
          elapsedMs: Date.now() - processingStartedAtMs,
          removePathsCount: removePaths.length,
          currentParamsCount,
          recentMessagesCount,
        },
        promptTelemetryForQueryOnly(trimmedUserQuery)
      );

      return {
        searchParams: mergedParams,
        intent: 'remove_filter',
        confidence: 0.99,
        needsClarification: false,
        clarification: undefined,
        message,
        followUpSuggestions: generateSuggestFromList(mergedParams).slice(0, 2),
        isReadyToSearch: Object.keys(mergedParams).length > 0,
        context: { accumulatedParams: mergedParams },
      };
    }

    if (detectDeterministicExecuteIntent(userQuery)) {
      const hasParams = Object.keys(currentParams).length > 0;

      logPhase2Debug('deterministic execute matched', {
        query: trimmedUserQuery,
        hasParams,
      });

      logNlpTelemetry(
        'path_decision',
        {
          path: 'phase2_execute_short_circuit',
          elapsedMs: Date.now() - processingStartedAtMs,
          currentParamsCount,
          recentMessagesCount,
        },
        promptTelemetryForQueryOnly(trimmedUserQuery)
      );

      if (!hasParams) {
        return {
          searchParams: { ...currentParams },
          intent: 'clarify',
          confidence: 0.95,
          needsClarification: true,
          clarification: 'Tell me a city or a filter first, then I can run the search.',
          followUpSuggestions: ['Try "houses in Miami"', 'Try "condos under 600k"'],
          isReadyToSearch: false,
          context: { accumulatedParams: { ...currentParams }},
        };
      }

      return {
        searchParams: { ...currentParams },
        intent: 'execute',
        confidence: 0.99,
        needsClarification: false,
        clarification: undefined,
        message: 'Searching now!',
        followUpSuggestions: [],
        isReadyToSearch: true,
        context: { accumulatedParams: { ...currentParams }},
      };
    }
  }

  if (!provider) {
    console.error('No AI provider provided');
    logNlpTelemetry(
      'path_decision',
      {
        path: 'provider_missing_short_circuit',
        elapsedMs: Date.now() - processingStartedAtMs,
        currentParamsCount,
        recentMessagesCount,
      },
      promptTelemetryForQueryOnly(trimmedUserQuery)
    );

    return {
      searchParams: currentParams,
      intent: 'clarify' as SearchIntent,
      confidence: 0.0,
      needsClarification: true,
      clarification: 'AI service is not configured. Please try basic search instead.',
      followUpSuggestions: ['Please contact support'],
      isReadyToSearch: false,
      context: { accumulatedParams: currentParams },
    };
  }

  try {
    const promptIntentHint = inferPromptIntentHint({
      query: trimmedUserQuery,
      currentParams,
      recentMessages,
    });
    const systemPrompt = buildSystemPrompt({ intentHint: promptIntentHint });

    // Build context message with only dynamic data (instructions are in system prompt)
    const currentDate = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format
    const hasExistingParams = Object.keys(currentParams).length > 0;
    
    // Generate prioritized list of suggestions based on missing filters
    const suggestFromList = generateSuggestFromList(currentParams);
    const suggestFromFormatted = suggestFromList.length > 0 
      ? suggestFromList.map((s, i) => `${i + 1}. ${s}`).join('\n')
      : 'All main filters are set. You can say "Anything else?" or leave followUpSuggestions empty.';
    
    const supportedCountriesList = SUPPORTED_COUNTRIES.map(c => `- ${c.name} (${c.code})`).join('\n');
    const supportedCountryCodes = SUPPORTED_COUNTRIES.map(c => c.code).join(', ');
    
    const contextMessage = hasExistingParams 
      ? `
CURRENT DATE: ${currentDate}
Use this date to calculate relative dates (e.g., "3 days ago", "last week", "listed in the last 30 days").

SUPPORTED COUNTRIES:
${supportedCountriesList}
If a user requests a location outside these countries (${supportedCountryCodes}), inform them that only these countries are supported.

CURRENT SEARCH PARAMS (for context):
${JSON.stringify(currentParams, null, 2)}

IMPORTANT: You only extract what the user says in THIS message.
- Return intent + newFilters
- Do NOT merge with currentParams (code handles that)
- Use history ONLY to understand context (like "yes" = confirm)
- Do NOT re-extract filters from history

================================================================================
FOLLOW-UP SUGGESTIONS - PICK FROM THIS LIST (prioritized, max 2):
================================================================================
${suggestFromFormatted}
================================================================================
Use natural language versions of these suggestions. Pick the top 1-2 that are most relevant.
If the list is empty or says "All main filters are set", use "Anything else?" or leave empty.
`
      : `
CURRENT DATE: ${currentDate}
Use this date to calculate relative dates (e.g., "3 days ago", "last week", "listed in the last 30 days").

SUPPORTED COUNTRIES:
${supportedCountriesList}
If a user requests a location outside these countries (${supportedCountryCodes}), inform them that only these countries are supported.

This is a new search with no previous context.

================================================================================
FOLLOW-UP SUGGESTIONS - PICK FROM THIS LIST (prioritized, max 2):
================================================================================
${suggestFromFormatted}
================================================================================
Use natural language versions of these suggestions. Pick the top 1-2 that are most relevant.
`;

    const fullSystemPrompt = systemPrompt + contextMessage;

    llmPromptMetrics = measurePromptTelemetry({
      systemPrompt,
      contextMessage,
      userQuery: trimmedUserQuery,
      promptIntentHint,
      recentMessages,
    });

    // Build messages array with conversation history
    const messages: Array<{ role: 'system' | 'user' | 'assistant'; content: string }> = [
      {
        role: 'system',
        content: fullSystemPrompt,
      },
    ];

    // Add recent conversation history if provided (already limited to 5 by frontend)
    if (recentMessages && recentMessages.length > 0) {
      recentMessages.forEach(msg => {
        messages.push({
          role: msg.role,
          content: msg.content,
        });
      });
    }

    // Add the current user query
    messages.push({
      role: 'user',
      content: userQuery,
    });

    logNlpTelemetry(
      'llm_request_prepared',
      {
        path: 'intent_routed_llm',
        elapsedMs: Date.now() - processingStartedAtMs,
        currentParamsCount,
        recentMessagesCount,
      },
      llmPromptMetrics
    );

    const llmRequestStartedAtMs = Date.now();
    const response = await provider.chat(messages, { jsonMode: true });
    const llmLatencyMs = Date.now() - llmRequestStartedAtMs;

    const aiResponse = response.content;

    if (!aiResponse) {
      throw new Error('No response from AI provider');
    }

    // Parse the JSON response
    // With JSON mode enabled, the response should be valid JSON directly
    let parsedResponse;
    try {
      // Try parsing directly first (JSON mode should return clean JSON)
      parsedResponse = JSON.parse(aiResponse);
    } catch (_directParseError) {
      // Fallback: Extract JSON from the response (in case there's extra text despite JSON mode)
      const jsonMatch = aiResponse.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        try {
          parsedResponse = JSON.parse(jsonMatch[0]);
        } catch (matchParseError) {
          console.error('❌ Failed to parse AI response:', matchParseError);
          logNlpTelemetry(
            'llm_parse_fallback',
            {
              reason: 'json_mode_response_invalid_json',
              llmLatencyMs,
              elapsedMs: Date.now() - processingStartedAtMs,
            },
            llmPromptMetrics
          );
          // Fallback to basic extraction
          const fallbackParams = extractBasicParameters(userQuery);
          const mergedParams = { ...currentParams, ...fallbackParams };
          
          return {
            searchParams: mergedParams,
            intent: 'clarify' as SearchIntent,
            confidence: 0.3,
            needsClarification: true,
            clarification: 'I had trouble understanding your request. Could you please rephrase it?',
            followUpSuggestions: ['Try "3 bedroom houses in Toronto under 800k"'],
            isReadyToSearch: false,
            context: { accumulatedParams: mergedParams },
          };
        }
      } else {
        console.error('❌ Failed to parse AI response: No JSON found');
        logNlpTelemetry(
          'llm_parse_fallback',
          {
            reason: 'no_json_object_in_response',
            llmLatencyMs,
            elapsedMs: Date.now() - processingStartedAtMs,
          },
          llmPromptMetrics
        );
        // Fallback to basic extraction
        const fallbackParams = extractBasicParameters(userQuery);
        const mergedParams = { ...currentParams, ...fallbackParams };
        
        return {
          searchParams: mergedParams,
          intent: 'clarify' as SearchIntent,
          confidence: 0.3,
          needsClarification: true,
          clarification: 'I had trouble understanding your request. Could you please rephrase it?',
          followUpSuggestions: ['Try "3 bedroom houses in Toronto under 800k"'],
          isReadyToSearch: false,
          context: { accumulatedParams: mergedParams },
        };
      }
    }

    // Parse the AI extraction (intent + newFilters)
    const aiExtraction: AIExtraction = {
      intent: (parsedResponse.intent as SearchIntent) || 'add_filter',
      newFilters: normalizeAiSearchParams(parsedResponse.newFilters || parsedResponse.searchParams || {}),
      removeFilters: parsedResponse.removeFilters || [],
      confidence: parsedResponse.confidence || 0.8,
      needsClarification: parsedResponse.needsClarification || false,
      clarification: parsedResponse.clarification,
      message: parsedResponse.message,
      followUpSuggestions: parsedResponse.followUpSuggestions || [],
    };

    // CODE-BASED MERGE: Apply newFilters based on intent
    let mergedParams = mergeFiltersBasedOnIntent(
      aiExtraction.intent,
      currentParams,
      aiExtraction.newFilters,
      aiExtraction.removeFilters
    );
    
    mergedParams = resolveLocationInSearchParams(mergedParams, userQuery);

    // Determine if ready to search
    const isReadyToSearch = !aiExtraction.needsClarification && 
      (aiExtraction.intent !== 'clarify') &&
      (Object.keys(mergedParams).length > 0);

    logNlpTelemetry(
      'llm_response_processed',
      {
        path: 'intent_routed_llm',
        llmLatencyMs,
        elapsedMs: Date.now() - processingStartedAtMs,
        intent: aiExtraction.intent,
        confidence: aiExtraction.confidence,
        needsClarification: aiExtraction.needsClarification,
        mergedParamsCount: Object.keys(mergedParams).length,
        removeFiltersCount: aiExtraction.removeFilters?.length || 0,
        newFiltersCount: Object.keys(aiExtraction.newFilters || {}).length,
      },
      llmPromptMetrics
    );

    return {
      searchParams: mergedParams,
      intent: aiExtraction.intent,
      confidence: aiExtraction.confidence,
      needsClarification: aiExtraction.needsClarification,
      clarification: aiExtraction.clarification,
      message: aiExtraction.message,
      followUpSuggestions: aiExtraction.followUpSuggestions,
      isReadyToSearch,
      context: { accumulatedParams: mergedParams },
    };

  } catch (error) {
    console.error('❌ Error processing query with AI provider:', error);
    logNlpTelemetry(
      'llm_error_fallback',
      {
        reason: error instanceof Error ? error.message : 'unknown_error',
        elapsedMs: Date.now() - processingStartedAtMs,
        currentParamsCount,
        recentMessagesCount,
      },
      llmPromptMetrics
    );
    
    // Fallback to basic extraction
    const fallbackParams = extractBasicParameters(userQuery);
    const mergedParams = { ...currentParams, ...fallbackParams };
    
    return {
      searchParams: mergedParams,
      intent: 'clarify' as SearchIntent,
      confidence: 0.2,
      needsClarification: true,
      clarification: 'I encountered an error processing your request. Please try a simpler query.',
      followUpSuggestions: ['Try "houses in Toronto" or "condos under 500k"'],
      isReadyToSearch: false,
      context: { accumulatedParams: mergedParams },
    };
  }
};


export const createNLPProcessor = (provider: AIProvider) => ({
  processQuery: (query: string, currentParams: SearchParameters = {}, recentMessages?: Array<{ role: 'user' | 'assistant'; content: string }>) => 
    processQuery(query, currentParams, provider, recentMessages),
  buildSystemPrompt,
  extractBasicParameters,
});